import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Heart, PackageCheck } from "lucide-react";
import api from "../lib/api";

const POLL_MS = 1200;
const MAX_POLLS = 20;

export default function MerchSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [tx, setTx] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let polls = 0;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            polls += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setTx(data);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
            } catch (e) {
                // polling — network hiccup expected during Stripe redirect
                if (process.env.NODE_ENV !== "production") console.debug("merch poll error", e);
            }
            if (polls >= MAX_POLLS) {
                setStatus("timeout");
                return;
            }
            setTimeout(tick, POLL_MS);
        };
        tick();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <div className="max-w-2xl mx-auto px-6 py-24">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <Card className="card-surface rounded-3xl p-10 text-center" data-testid="merch-success-card">
                    {status === "polling" && (
                        <>
                            <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center animate-pulse">
                                <PackageCheck className="h-7 w-7 text-primary" />
                            </div>
                            <h1 className="font-display text-3xl md:text-4xl text-accent mt-6">Finalizing your pre-order…</h1>
                            <p className="text-muted-foreground mt-3">Hold tight — we&#39;re confirming the payment with Stripe.</p>
                        </>
                    )}
                    {status === "paid" && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                                <CheckCircle2 className="h-9 w-9 text-emerald-400" />
                            </div>
                            <h1 className="font-display text-3xl md:text-4xl text-accent mt-6" data-testid="merch-success-heading">
                                You&#39;re in. Pre-order confirmed.
                            </h1>
                            <p className="text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">
                                Half of your <strong className="text-accent">${tx?.amount?.toFixed?.(2) || "—"}</strong> goes to
                                <span className="text-emerald-400 font-semibold"> Hands of Hope</span>. We&#39;ll email you the second your item ships.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3 justify-center">
                                <Link to="/shop">
                                    <Button variant="secondary" className="rounded-full" data-testid="merch-success-back-shop">
                                        Back to the shop
                                    </Button>
                                </Link>
                                <Link to="/donate">
                                    <Button className="rounded-full bg-primary text-primary-foreground" data-testid="merch-success-donate">
                                        <Heart className="h-4 w-4 mr-2" /> Add a direct donation
                                    </Button>
                                </Link>
                            </div>
                        </>
                    )}
                    {(status === "timeout" || status === "expired" || status === "error") && (
                        <>
                            <h1 className="font-display text-3xl text-accent mt-2">We couldn&#39;t confirm yet</h1>
                            <p className="text-muted-foreground mt-3">
                                Your card may still be processing. Check your email in a few minutes — if you don&#39;t see a confirmation, contact us.
                            </p>
                            <Link to="/shop" className="inline-block mt-6">
                                <Button variant="secondary" className="rounded-full">Back to the shop</Button>
                            </Link>
                        </>
                    )}
                </Card>
            </motion.div>
        </div>
    );
}
